import React from 'react';
import { Button } from './ui/button';

interface OfferFormProps {
  formData: {
    name: string;
    description: string;
    vertical_id: string;
    country_id: string;
    status: string;
    provider_id: string;
    client_id: string;
  };
  setFormData: (data: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  filterOptions: {
    verticals: Array<{id: string, name: string}>;
    countries: Array<{id: string, name: string}>;
    providers: Array<{id: string, name: string}>;
    clients: Array<{id: string, name: string}>;
    statuses: string[];
  };
  submitText?: string;
  isEditing?: boolean;
}

export default function OfferForm({
  formData,
  setFormData,
  onSubmit,
  onCancel,
  filterOptions,
  submitText = "Create Offer",
  isEditing = false
}: OfferFormProps) {
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Offer Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          required
          className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={formData.name}
          onChange={(e) => setFormData({...formData, name: e.target.value})}
          placeholder="Enter offer name"
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Description</label>
        <textarea
          className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          value={formData.description}
          onChange={(e) => setFormData({...formData, description: e.target.value})}
          placeholder="Enter offer description (optional)"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Vertical <span className="text-red-500">*</span>
          </label>
          <select
            required
            className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            value={formData.vertical_id}
            onChange={(e) => setFormData({...formData, vertical_id: e.target.value})}
          >
            <option value="">Select Vertical</option>
            {filterOptions.verticals.map(vertical => (
              <option key={vertical.id} value={vertical.id}>
                {vertical.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Country <span className="text-red-500">*</span>
          </label>
          <select
            required
            className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            value={formData.country_id}
            onChange={(e) => setFormData({...formData, country_id: e.target.value})}
          >
            <option value="">Select Country</option>
            {filterOptions.countries.map(country => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Provider <span className="text-red-500">*</span>
          </label>
          <select
            required
            disabled={isEditing}
            className={`w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditing ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            value={formData.provider_id}
            onChange={(e) => setFormData({...formData, provider_id: e.target.value})}
          >
            <option value="">Select Provider</option>
            {filterOptions.providers.map(provider => (
              <option key={provider.id} value={provider.id}>
                {provider.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Client <span className="text-red-500">*</span>
          </label>
          <select
            required
            disabled={isEditing}
            className={`w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${isEditing ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            value={formData.client_id}
            onChange={(e) => setFormData({...formData, client_id: e.target.value})}
          >
            <option value="">Select Client</option>
            {filterOptions.clients.map(client => (
              <option key={client.id} value={client.id}>
                {client.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Status <span className="text-red-500">*</span>
        </label>
        <select
          className="w-full rounded-lg border border-gray-300 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          value={formData.status}
          onChange={(e) => setFormData({...formData, status: e.target.value})}
        >
          {filterOptions.statuses.map(status => (
            <option key={status} value={status}>
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>
      </div>

      <div className="flex gap-3 pt-4">
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700 flex-1 cursor-pointer">
          {submitText}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="flex-1 cursor-pointer"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}